import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

/**
 * Environment-gated logging service.
 *
 * Usage:
 *   private logger = inject(LoggingService);
 *   this.logger.log('Loaded rules', rules);
 *   this.logger.warn('Slow response', duration);
 *   this.logger.error('API failed', err);
 *
 * log / info / warn  → dev-only (stripped in production builds).
 * error              → always emitted so HTTP failures and guard errors remain
 *                      visible in production logs / browser devtools.
 *                      Wire a remote tracker (Sentry / Datadog) here once
 *                      available — all call sites stay unchanged.
 */
@Injectable({ providedIn: 'root' })
export class LoggingService {
  private readonly isProd = environment.production;

  log(message: string, ...data: unknown[]): void {
    if (!this.isProd) {
      // eslint-disable-next-line no-console
      console.log(`[LOG] ${message}`, ...data);
    }
  }

  info(message: string, ...data: unknown[]): void {
    if (!this.isProd) {
      // eslint-disable-next-line no-console
      console.info(`[INFO] ${message}`, ...data);
    }
  }

  warn(message: string, ...data: unknown[]): void {
    if (!this.isProd) {
      // eslint-disable-next-line no-console
      console.warn(`[WARN] ${message}`, ...data);
    }
  }

  /**
   * Always emitted — errors must be visible in production.
   * TODO: add remote tracker here (Sentry / Datadog) once provisioned.
   *   e.g. if (this.isProd) { Sentry.captureException(data[0]); }
   */
  error(message: string, ...data: unknown[]): void {
    // eslint-disable-next-line no-console
    console.error(`[ERROR] ${message}`, ...data);
  }
}
